/* eslint-disable no-restricted-syntax */
import axios from 'axios';
import L, { Map, TileLayer, geoJSON } from 'leaflet';
import 'src/libs/leaflet.edgebuffer';
import 'src/libs/Leaflet.TileLayer.Fallback/src/fallback';
import 'src/libs/L.TileLayer.WMTS/leaflet.tilelayer.wmts.min';
import { urlsConfig } from 'src/configs';
import {
  TLayers,
  ITileLayer,
  IWMSLayer,
  IVectorLayer,
  defaultStyle,
  s57LayerPolygonStyle,
  s57LayerStyle,
  getCRSFromString,
  IWMTSLayer,
} from './config';

const vectorLayersSources: Record<string, L.GeoJSON> = {};

const tileLayersSources: Record<string, L.TileLayer> = {};

let layerControl: L.Control | undefined;

export class LayersController {
  minimapLayer: L.TileLayer | undefined;

  minimapControl: L.Control.MiniMap | undefined;

  axiosInstance = axios.create({
    baseURL: urlsConfig.fileServerBaseUrl,
  });

  layersSource: TLayers = [];

  activeLayer: string | undefined;

  static instance: LayersController | null = null;

  static getInstance(): LayersController {
    if (this.instance === null) {
      this.instance = new LayersController();
    }
    return this.instance;
  }

  setLayersSource = async (data: TLayers, map: L.Map) => {
    this.layersSource = data;

    this.setTileLayers(
      this.layersSource.filter((o) => o.layerType === 'tile' && o.isBaseLayer && o.isActive) as ITileLayer[],
      this.layersSource.filter((o) => o.layerType === 'wms' && o.isBaseLayer && o.isActive) as IWMSLayer[],
      this.layersSource.filter((o) => o.layerType === 'wmts' && o.isBaseLayer && o.isActive) as IWMTSLayer[],
    );

    this.fetchBaseVectors(
      this.layersSource.filter((o) => o.layerType === 'geojson' && o.isBaseLayer && o.isActive) as IVectorLayer[],
    );
    this.fetchOverlayVectors(
      this.layersSource.filter((o) => o.layerType === 'geojson' && !o.isBaseLayer && o.isActive) as IVectorLayer[],
      map,
    );

    if (this.activeLayer) {
      this.handleLayerChange(this.activeLayer, map);
    }
  };

  fetchBaseVectors = async (layers: IVectorLayer[]) => {
    for (const layer of layers) {
      try {
        if (layer.id) {
          let data;
          if (layer.filePath && layer.filePath !== '') {
            data = (await this.axiosInstance.get(`${urlsConfig.layersServerURL}${layer.filePath}`)).data;
          } else if (layer.url) {
            data = (await this.axiosInstance.get(layer.url)).data;
          }

          if (data) {
            vectorLayersSources[layer.id] = geoJSON(data, {
              style: defaultStyle(false, layer.geometryColor),
              attribution: layer.attribution,
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch vector map');
      }
    }
  };

  fetchOverlayVectors = async (layers: IVectorLayer[], map: L.Map) => {
    const overlays: Record<string, L.GeoJSON> = {};

    for (const layer of layers) {
      if (layer.id && layer.filePath && layer.filePath !== '') {
        try {
          const { data } = await this.axiosInstance.get(`${urlsConfig.layersServerURL}${layer.filePath}`);

          if (data) {
            const style =
              layer.geometryType === 'Polygon'
                ? {
                    ...s57LayerPolygonStyle,
                    fillColor: layer.geometryColor,
                  }
                : {
                    ...s57LayerStyle,
                    fillColor: layer.geometryColor,
                  };

            let geojsonLayer;

            switch (layer.geometryType) {
              case 'Point':
                geojsonLayer = geoJSON(data, {
                  style,
                  // onEachFeature,
                  pointToLayer: (_feature, latlng) => {
                    return L.circleMarker(latlng, {
                      ...style,
                      radius: 4,
                    });
                  },
                });

                break;

              case 'Polygon':
                geojsonLayer = geoJSON(data, {
                  // onEachFeature,
                  style,
                });

                break;

              case 'Polyline':
                geojsonLayer = geoJSON(data, {
                  // onEachFeature,
                  style,
                });

                break;

              default:
                break;
            }

            if (geojsonLayer) {
              overlays[layer.label] = geojsonLayer;
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch vector map for: ', layer.label);
        }
      }
    }

    if (Object.keys(overlays).length) {
      layerControl?.remove();
      layerControl = L.control.layers({}, overlays, { collapsed: false });
      layerControl.addTo(map);
    }
  };

  loadImage = async (src: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  getColorAtLatLng = async (latlng: L.LatLng, map: L.Map, tileLayer: TileLayer) => {
    const zoom = map.getZoom();
    const tileSize = tileLayer.getTileSize().x;
    const p = map.project(latlng, zoom).round();

    const tileX = Math.floor(p.x / tileSize);
    const tileY = Math.floor(p.y / tileSize);
    const pxDiff = p.x - tileX * tileSize;
    const pyDiff = p.y - tileY * tileSize;

    let imgElement: any;
    // @ts-ignore
    const tileElements = Object.values(tileLayer._tiles || {});

    const foundTile = tileElements.find(
      (t) => t && t.coords && t.coords.z === zoom && t.coords.x === tileX && t.coords.y === tileY,
    );

    // @ts-ignore
    if (foundTile && foundTile.el && foundTile.el.tagName === 'IMG' && foundTile.el.complete) {
      imgElement = foundTile.el;
    } else {
      const url = tileLayer.getTileUrl({ x: tileX, y: tileY, z: zoom } as L.Coords);
      imgElement = await this.loadImage(url);
    }

    const width = imgElement.naturalWidth || imgElement.width;
    const height = imgElement.naturalHeight || imgElement.height;

    const offScreenCanvas = document.createElement('canvas');

    const ctx = offScreenCanvas.getContext('2d');

    if (ctx) {
      offScreenCanvas.width = width;
      offScreenCanvas.height = height;

      ctx.clearRect(0, 0, width, height);

      ctx.drawImage(imgElement, 0, 0);

      const scaleX = width / tileSize;
      const scaleY = height / tileSize;

      const x = Math.floor(pxDiff * scaleX);
      const y = Math.floor(pyDiff * scaleY);

      const { data } = ctx.getImageData(x, y, 1, 1);
      // @ts-ignore
      const [r, g, b, a255] = data;

      const a = +(a255 / 255).toFixed(3);

      return { r, g, b, a };
    }
  };

  setTileLayers = (tileLayers: ITileLayer[], wmsLayers: IWMSLayer[], wmtsLayers: IWMTSLayer[]) => {
    tileLayers.forEach((layerDetails) => {
      if (layerDetails.id) {
        const { url } = layerDetails;
        const options = {
          edgeBufferTiles: 1,
          attribution: layerDetails.attribution,
          noWrap: true,
          bounds: [
            [-85, -180],
            [85, 180],
          ],
          crossOrigin: 'anonymous',
        };
        const existingLayer = tileLayersSources[layerDetails.id];
        if (existingLayer) {
          existingLayer.remove();
        }

        tileLayersSources[layerDetails.id] = L.tileLayer.fallback(url, options) as L.TileLayer;
      }
    });

    wmsLayers.forEach((layerDetails) => {
      if (layerDetails.id) {
        const { url } = layerDetails;

        const options: L.WMSOptions & { tiled?: string } = {
          layers: layerDetails.wmsLayer,
          format: layerDetails.format,
          transparent: layerDetails.transparent,
          version: layerDetails.version,
          crs: getCRSFromString(layerDetails.crs),
          attribution: layerDetails.attribution,
        };

        if (layerDetails.tiled) {
          options.tiled = 'true';
        }

        const existingLayer = tileLayersSources[layerDetails.id];
        if (existingLayer) {
          existingLayer.remove();
        }

        tileLayersSources[layerDetails.id] = L.tileLayer.wms(url, options);
      }
    });

    wmtsLayers.forEach((layerDetails) => {
      if (layerDetails.id) {
        const { url } = layerDetails;

        const options: L.WMTSOptions & { tiled?: string } = {
          layer: layerDetails.wmtsLayer,
          format: layerDetails.format,
          version: layerDetails.version,
          tileMatrixSet: getCRSFromString(layerDetails.crs)?.code || 'EPSG:4326',
          attribution: layerDetails.attribution,
        };

        const existingLayer = tileLayersSources[layerDetails.id];
        if (existingLayer) {
          existingLayer.remove();
        }

        // @ts-ignore
        tileLayersSources[layerDetails.id] = L.tileLayer.wmts(url, options);
      }
    });
  };

  toggleVectorLayer = (id: string, onOff: boolean, map: L.Map) => {
    if (vectorLayersSources) {
      if (onOff) {
        if (!map.hasLayer(vectorLayersSources[id])) {
          vectorLayersSources[id]?.addTo(map).bringToBack();
        }
      } else {
        vectorLayersSources[id]?.remove();
      }
    }
  };

  toggleTileLayer = (id: string, onOff: boolean, map: L.Map) => {
    if (tileLayersSources[id] !== undefined) {
      if (onOff) {
        if (!map.hasLayer(tileLayersSources[id])) {
          tileLayersSources[id].addTo(map).bringToBack();
        } else {
          tileLayersSources[id].remove();
        }
      } else {
        tileLayersSources[id].remove();
      }
    }
  };

  setMinimapLayer = (id: string) => {
    const layerDetails = this.layersSource.filter((o) => o.layerType === 'tile').find((o) => o.id === id);

    if (layerDetails) {
      this.minimapLayer = L.tileLayer.fallback(layerDetails.url, { minZoom: 0, maxZoom: 13 }) as L.TileLayer;
      this.minimapControl = new L.Control.MiniMap(this.minimapLayer, { position: 'bottomleft' });
    }
  };

  changeMinimapUrl = (id: string) => {
    const layerDetails = this.layersSource.filter((o) => o.layerType === 'tile').find((o) => o.id === id);

    if (layerDetails && layerDetails.url) {
      this.minimapLayer?.setUrl(layerDetails.url);
    }
  };

  toggleMinimap = (onOff: boolean, map: L.Map, activeLayer?: string) => {
    if (!this.minimapControl && activeLayer) {
      this.setMinimapLayer(activeLayer);
    }

    if (onOff) {
      this.minimapControl?.addTo(map);
    } else {
      this.minimapControl?.remove();
    }
  };

  removeBaseLayers = () => {
    Object.entries(vectorLayersSources).forEach(([key, layer]) => {
      layer.remove();
    });

    Object.entries(tileLayersSources).forEach(([key, layer]) => {
      layer.remove();
    });
  };

  handleLayerChange = async (id: string, map: L.Map) => {
    const layer = this.layersSource.find((o) => o.id === id);
    const layerToActivate = vectorLayersSources[id] || tileLayersSources[id];

    if (layerToActivate !== undefined) {
      this.removeBaseLayers();
      Object.values(vectorLayersSources).forEach((layerToRemove) => layerToRemove.remove());
      Object.values(tileLayersSources).forEach((layerToRemove) => layerToRemove.remove());

      this.activeLayer = id;
      layerToActivate.addTo(map).bringToBack();
    }
    if (layer?.layerType === 'geojson') {
      this.toggleMinimap(false, map, id);
      layerToActivate?.bringToBack();
    } else {
      const configLatLng = {
        lat: !Number(window.HYMOTS_C_MAP_COLOR_LAT) ? 80 : Number(window.HYMOTS_C_MAP_COLOR_LAT),
        lng: !Number(window.HYMOTS_C_MAP_COLOR_LNG) ? 170 : Number(window.HYMOTS_C_MAP_COLOR_LNG),
      };
      const data = await this.getColorAtLatLng(
        L.latLng(configLatLng.lat, configLatLng.lng),
        map,
        tileLayersSources[id],
      );
      const foundMapElement = document.getElementById('map');

      if (foundMapElement && data && data.r && data.g && data.b) {
        foundMapElement.style.background = `rgba(${data?.r}, ${data?.g}, ${data?.b}, ${data?.a})`;
      }

      this.changeMinimapUrl(id);
      this.toggleMinimap(true, map, id);
    }
  };
}
